"use client";

import { TalkToken } from "@/app/_module/components/DialCreditsIndicator";
import {
  User,
  Phone,
  MessageSquare,
  CreditCard,
  Crown,
  Shield,
  CheckCircle,
  Building2,
  Heart,
  Palette,
  // Building,
} from "lucide-react";
import { useSearchParams, useRouter } from "next/navigation";
import { Suspense, useEffect, useState } from "react";

import { isNativePlatform } from "@/lib/capacitor/platform";
import { WebOnlyNotice } from "@/components/mobile/WebOnlyNotice";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { accountTypeHasTelephony } from "@/app/_module/components/TelephonyEnabled";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";

import { AccountSettingsWithTabs } from "@/app/(authenticated)/settings/_modules/components/account/AccountSettingsWithTabs";
// import { AgencyStats } from "@/app/(authenticated)/settings/_modules/components/AgencyStats";
import { AgencyBillingSettings } from "@/app/(authenticated)/settings/_modules/components/AgencyBillingSettings";
import { UnifiedAreaCodeManagement } from "@/app/(authenticated)/settings/_modules/components/UnifiedAreaCodeManagement";
import DialCreditsPanel from "@/app/(authenticated)/settings/_modules/components/DialCreditsPanel";
// import { PhoneNumberManagement } from "@/app/(authenticated)/settings/_modules/components/PhoneNumberManagement";
import { PurchaseHistory } from "@/app/(authenticated)/settings/_modules/components/PurchaseHistory";
import { RecurringCharges } from "@/app/(authenticated)/settings/_modules/components/RecurringCharges";
import { SMSTemplateSettings } from "@/app/(authenticated)/settings/_modules/components/SMSTemplateSettings";
import { SubscriptionPlans } from "@/app/(authenticated)/settings/_modules/components/SubscriptionPlans";
import { CarriersSettings } from "@/app/(authenticated)/settings/_modules/components/CarriersSettings";
import { SentimentalsSettings } from "@/app/(authenticated)/settings/_modules/components/SentimentalsSettings";
import { ContactCardSettings } from "@/app/(authenticated)/settings/_modules/components/ContactCardSettings";
import { BrandingSettings } from "@/app/(authenticated)/settings/_modules/components/BrandingSettings";
import { useCanEditBranding } from "@/app/(authenticated)/settings/_modules/components/OwnerBranding";
import dynamic from "next/dynamic";

const A2PVerificationPage = dynamic(() => import("./a2p-verification/page"), {
  ssr: false,
});
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";

// Static class names so Tailwind keeps them (it can't see computed strings).
const TAB_GRID_COLS: Record<number, string> = {
  4: 'md:grid-cols-4', 5: 'md:grid-cols-5', 6: 'md:grid-cols-6',
  7: 'md:grid-cols-7', 8: 'md:grid-cols-8', 9: 'md:grid-cols-9',
};

const SettingsContent = function () {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useUserContext();
  const userRole = user?.currentAgency?.role || "user";
  const isAgencyOwner = userRole === "owner" || userRole === "admin" || userRole === "agency_owner" || userRole === "agency-owner";
  // Enterprise owners, and owners under one that lets them brand their agency
  const canEditBranding = useCanEditBranding();
  // Dial Credits tab is super_agent only (paid AI Outbound Calling feature)
  const isSuperAgent = user?.user?.accountType === 'super_agent';

  // Lite (or a trial of Lite) has no phone: no Area Codes tab, and a deep link
  // to it lands on Subscription, where the upgrade lives.
  const hasTelephony = accountTypeHasTelephony(user?.user?.accountType, (user?.user as any)?.trialAccountTypeUsed);

  // Direct 130's agents are full-fledged at no charge (they pay only for their numbers): no Subscriptions tab,
  // and its deep links (upgrade prompts, ?tab=subscription) land on Account.
  const onDirect130 = useDirect130Host();

  // Get the tab from URL parameters, default to "account"
  const requestedTab = searchParams.get("tab") || "account";
  const phoneOnlyTabs = ["area-codes", "sms-settings", "a2p-verification"];
  const tab = !hasTelephony && phoneOnlyTabs.includes(requestedTab) ? "subscription" : requestedTab;
  const defaultTab = onDirect130 && tab === "subscription" ? "account" : tab;
  // Dial Minutes: super agents' AI Calling credits, and Direct 130's dialing credits (they top up there).
  const showCredits = isSuperAgent || onDirect130;
  // Get the SMS sub-tab from URL parameters, default to "templates"
  const smsSubTab = searchParams.get("smsTab") || "templates";

  // Native app: billing/purchase tabs are web-only (store-review safety).
  const [isNativeApp, setIsNativeApp] = useState(false);
  useEffect(() => {
    setIsNativeApp(isNativePlatform());
  }, []);

  // Shell-aware base path — this page is also embedded at /m/settings
  const settingsBase =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m")
      ? "/m/settings"
      : "/settings";

  // Handle tab change and update URL
  const handleTabChange = (value: string) => {
    // Preserve smsTab when switching to sms-settings, clear it otherwise
    if (value === "sms-settings") {
      router.replace(`${settingsBase}?tab=${value}&smsTab=${smsSubTab}`, { scroll: false });
    } else {
      router.replace(`${settingsBase}?tab=${value}`, { scroll: false });
    }
  };

  // Handle SMS sub-tab change
  const handleSmsSubTabChange = (value: string) => {
    router.replace(`${settingsBase}?tab=sms-settings&smsTab=${value}`, { scroll: false });
  };

  // Determine if user is in a sub-agency context
  const currentAgency = user?.currentAgency?.agency;
  // Check both camelCase and snake_case for parent agency ID (backend may use snake_case)
  const parentId = (currentAgency as any)?.parentAgencyId || (currentAgency as any)?.parent_agency_id;
  const isSubAgency = parentId !== undefined && parentId !== null;
  const agencyName = currentAgency?.name || '';

  const pageTitle = 'Settings';
  const pageDescription = onDirect130
    ? 'Manage your account settings and preferences'
    : 'Manage your account settings, preferences, and subscription';

  return (
    <div className="space-y-6 overflow-hidden">
      <div data-tour="settings-header">
        <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
        <p className="text-gray-600 mt-2">
          {pageDescription}
        </p>
      </div>

      <Tabs className="space-y-6" value={defaultTab} onValueChange={handleTabChange}>
        <TabsList
          data-tour="settings-tabs"
          className={`flex w-full min-w-0 gap-1 md:grid ${
            // Tailwind needs static class names — look the count up in a
            // precompiled map. 7 base tabs; +1 Branding (canEditBranding),
            // +1 Dial Credits (super agent, Direct 130); −3 without a phone (Lite); −1 Subscriptions on Direct 130.
            TAB_GRID_COLS[7 + (canEditBranding ? 1 : 0) + (showCredits ? 1 : 0) - (hasTelephony ? 0 : 3) - (onDirect130 ? 1 : 0)] ?? 'md:grid-cols-7'
          } md:justify-center scrollbar-hide`}
        >
          <TabsTrigger
            className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
            value="account"
            data-tour="settings-tab-account"
          >
            <User className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">Account</span>
          </TabsTrigger>
          {hasTelephony && (
          <TabsTrigger
            className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
            value="area-codes"
            data-tour="settings-tab-area-codes"
          >
            <Phone className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">Area Codes</span>
          </TabsTrigger>
          )}
          {hasTelephony && (
          <TabsTrigger
            className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
            value="sms-settings"
            data-tour="settings-tab-sms"
          >
            <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">SMS Settings</span>
          </TabsTrigger>
          )}
          {hasTelephony && (
          <TabsTrigger
            className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
            value="a2p-verification"
            data-tour="settings-tab-a2p"
          >
            <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">A2P / 10DLC</span>
          </TabsTrigger>
          )}
          <TabsTrigger
            className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
            value="carriers"
            data-tour="settings-tab-carriers"
          >
            <Building2 className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">Carriers</span>
          </TabsTrigger>
          {/*{!!isAgencyOwner && (
            <TabsTrigger
              className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2"
              value="agency-dashboard"
            >
              <Building className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="hidden sm:inline">Agency Stats</span>
            </TabsTrigger>
          )}*/}
          {canEditBranding && (
            <TabsTrigger
              className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
              value="branding"
              data-tour="settings-tab-branding"
            >
              <Palette className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="hidden sm:inline whitespace-nowrap">Branding</span>
            </TabsTrigger>
          )}
          <TabsTrigger
            className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
            value="purchase-history"
            data-tour="settings-tab-purchases"
          >
            <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">Purchases</span>
          </TabsTrigger>
          {!onDirect130 && (
            <TabsTrigger
              className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
              value="subscription"
              data-tour="settings-tab-subscription"
            >
              <Crown className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="hidden sm:inline whitespace-nowrap">Subscriptions</span>
            </TabsTrigger>
          )}
          {showCredits && (
            <TabsTrigger
              className="flex items-center gap-1.5 text-xs sm:text-sm px-1.5 sm:px-2 py-2 flex-shrink-0"
              value="dial-credits"
              data-tour="settings-tab-dial-credits"
            >
              <TalkToken className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="hidden sm:inline whitespace-nowrap">Dial Minutes</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="account">
          <AccountSettingsWithTabs />
        </TabsContent>

        {hasTelephony && (
        <TabsContent value="area-codes">
          <UnifiedAreaCodeManagement />
        </TabsContent>
        )}


        {hasTelephony && (
        <TabsContent value="sms-settings">
          <Tabs value={smsSubTab} onValueChange={handleSmsSubTabChange} className="w-full">
            <TabsList className="m-subtabs-fill flex w-full min-w-0 overflow-x-auto gap-1 md:grid md:grid-cols-3 md:justify-center mb-6 scrollbar-hide" data-tour="settings-sms-subtabs">
              <TabsTrigger value="templates" data-tour="settings-sms-templates">Templates</TabsTrigger>
              <TabsTrigger value="sentimentals" data-tour="settings-sms-sentimentals">Sentimentals</TabsTrigger>
              <TabsTrigger value="contact-card">Contact Card</TabsTrigger>
            </TabsList>
            <TabsContent value="templates">
              <SMSTemplateSettings />
            </TabsContent>
            <TabsContent value="sentimentals">
              <SentimentalsSettings />
            </TabsContent>
            <TabsContent value="contact-card">
              <ContactCardSettings />
            </TabsContent>
          </Tabs>
        </TabsContent>
        )}

        {hasTelephony && (
        <TabsContent value="a2p-verification">
          <A2PVerificationPage />
        </TabsContent>
        )}

        <TabsContent value="carriers">
          <CarriersSettings />
        </TabsContent>

        {/*{!!isAgencyOwner && (
          <TabsContent value="agency-dashboard">
            <AgencyStats />
          </TabsContent>
        )}*/}

        {canEditBranding && (
          <TabsContent value="branding">
            <BrandingSettings />
          </TabsContent>
        )}

        {showCredits && (
          <TabsContent value="dial-credits">
            <DialCreditsPanel />
          </TabsContent>
        )}

        <TabsContent value="purchase-history">
          {isNativeApp ? (
            <WebOnlyNotice
              icon={CreditCard}
              title="Purchase History"
              message="Purchase history and recurring charges are available on the web version of InsuraCentral. To review them, log in on your computer at app.insuracentral.com."
            />
          ) : (
            <div className="space-y-6">
              <PurchaseHistory />
              <RecurringCharges />
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscription">
          {isAgencyOwner && currentAgency?.id ? (
            <AgencyBillingSettings agencyId={currentAgency.id} />
          ) : (
            <SubscriptionPlans isAgencyOwner={isAgencyOwner} />
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
};

const Settings = function () {
  return (
    <Suspense fallback={<div>Loading settings...</div>}>
      <SettingsContent />
    </Suspense>
  );
};

export default Settings;
